import { getPref, setPref, subscribePref } from "./userPrefs";

/**
 * Per-user guest-detail LAYOUT choice, scoped per (event, view-context).
 *
 * Key shape: `gdl-<eventId>-<viewId|DEFAULT>` — the view scope makes a manual
 * pick an override *of the current view context* rather than a global sticky
 * pref: picking layout X while view A (linked to layout Y) is active must win
 * over the link visibly (otherwise the picker is a dead control), but
 * switching to view B must restore B's own link, and a pick made months ago
 * must not shadow every future view link. Standalone popup surfaces (seating,
 * slotting, AI navigation) always run under the DEFAULT view, so they resolve
 * consistently via the DEFAULT scope.
 *
 * `userPrefs` is NOT same-tab reactive (the `storage` event fires only in
 * other tabs), but the popup picker, the layout view, the sidebar tabs and
 * the action bar must all re-render on selection — hence the module-level
 * listener set + `useSyncExternalStore`-compatible subscribe.
 */

/** Sentinel id for the unnamed Standard layout (Event.guestDetailLayout).
 *  Reserved server-side — a named layout can never claim it. */
export const GDL_STANDARD = "default";

/** View-context scope for surfaces with no user-selected view. */
export const GDL_DEFAULT_VIEW_SCOPE = "DEFAULT";

function prefKey(eventId: string, viewScope: string): string {
  return `gdl-${eventId}-${viewScope}`;
}

/** Normalizes an active view id to a pref scope (no view / DEFAULT view → DEFAULT). */
export function viewScopeOf(activeViewId: string | null | undefined): string {
  return activeViewId && activeViewId.length > 0 ? activeViewId : GDL_DEFAULT_VIEW_SCOPE;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

/** Selected layout id for this (event, view-context), or null = no choice made. */
export function getLayoutPref(eventId: string, viewScope: string): string | null {
  return getPref(prefKey(eventId, viewScope));
}

/** Persist a pick (GDL_STANDARD or a named layout id) and notify this tab. */
export function setLayoutPref(eventId: string, viewScope: string, layoutId: string): void {
  setPref(prefKey(eventId, viewScope), layoutId);
  notify();
}

/**
 * Subscribe to layout-pref changes: same-tab via the local listener set,
 * cross-tab via the storage event on this exact key. `useSyncExternalStore`
 * compatible. Returns an unsubscribe function.
 */
export function subscribeLayoutPref(
  eventId: string,
  viewScope: string,
  listener: Listener,
): () => void {
  listeners.add(listener);
  const unsubCrossTab = subscribePref(prefKey(eventId, viewScope), () => listener());
  return () => {
    listeners.delete(listener);
    unsubCrossTab();
  };
}
